import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Download, Send } from "lucide-react";

import { getAll } from "../services/pagination";
import { timetableService, type TimetablePeriod, type TimetableRequest, type TimetableSlot } from "../services/timetableService";
import { useAuthStore } from "../stores/authStore";
import { apiErrorMessage } from "../utils/apiError";
import AIAssistField from "../components/AIAssistField";
import { Field, FormSection } from "../components/FormLayout";
import { FormModal } from "../components/FormModal";
import { ClientRegister, type RegisterColumn } from "../components/register/ClientRegister";
import { DetailsModal } from "../components/register/DetailsModal";
import { Notice, type NoticeState } from "../components/register/RecordParts";
import { RequestsPanel } from "../components/timetable/RequestsPanel";
import { WeekGrid } from "../components/timetable/WeekGrid";
import { PeriodGrid } from "../components/timetable/PeriodGrid";
import { LayoutSwitch, useWeekLayout } from "../components/timetable/LayoutSwitch";
import { DAYS, dayName, isLesson, lessonConfig, levelExcluded, periodTime } from "../components/timetable/timetableLogic";

const TEACHING_ROLES = new Set(["teacher", "class_teacher", "head_of_department"]);
const REQUESTABLE: [TimetableRequest["request_type"], string, string][] = [
  ["change_time", "Move it to another day or period", "Choose the day and period you need."],
  ["change_room", "Move it to another room", "Say which room."],
  ["remove_slot", "Take it off the timetable", "The lesson stops appearing for the class and for you."],
];

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

/** Your week: a teacher's own lessons, or the classes a learner or parent follows. Teachers can ask for a change. */
export default function MyTimetable() {
  const user = useAuthStore((state) => state.user);
  const canRequest = TEACHING_ROLES.has(user?.user_type ?? "");
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<TimetableSlot | null>(null);
  const [asking, setAsking] = useState<{ slot?: number } | null>(null);
  const [layout, setLayout] = useWeekLayout();
  const [classChoice, setClassChoice] = useState<number | null>(null);

  const slots = useQuery({
    queryKey: ["my-timetable", user?.id, canRequest],
    // A class teacher's own list must be their own lessons, not the whole
    // class's: the server's role scope includes every lesson in a class they
    // are class teacher of, so a teacher narrows to their own teacher_id.
    queryFn: () => getAll<TimetableSlot>("/timetable/slots/", canRequest && user ? { teacher_id: user.id } : undefined),
    enabled: !!user,
  });
  const records = useMemo(() => slots.data ?? [], [slots.data]);
  const firstYear = records[0]?.academic_year;
  const periods = useQuery({ queryKey: ["my-timetable-periods", firstYear], queryFn: () => timetableService.getPeriods(firstYear), enabled: Boolean(firstYear) });
  const periodList = useMemo(() => periods.data ?? [], [periods.data]);
  const periodMap = useMemo(() => new Map(periodList.map((period) => [period.id, period])), [periodList]);
  const requests = useQuery({ queryKey: ["my-timetable-requests"], queryFn: () => timetableService.getRequests(), enabled: canRequest });
  const submit = useMutation({
    mutationFn: timetableService.createRequest,
    onSuccess: () => {
      setAsking(null);
      setNotice({ tone: "success", text: "Your request was sent to the Director of Studies. You'll see the decision below." });
      queryClient.invalidateQueries({ queryKey: ["my-timetable-requests"] });
    },
  });

  const classes = useMemo(() => {
    if (canRequest) return [];
    const seen = new Map<number, string>();
    records.forEach((slot) => seen.set(slot.class_assigned, slot.class_name));
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [records, canRequest]);
  // A parent with children in different classes looks at one class at a time.
  const shownClass = classes.length > 1 ? classes.find((item) => item.id === classChoice) ?? classes[0] : classes[0];
  const shown = shownClass && classes.length > 1 ? records.filter((slot) => slot.class_assigned === shownClass.id) : records;
  const classLevel = canRequest ? undefined : shown[0]?.class_level;
  const download = async (fetchPdf: () => Promise<Blob>, filename: string) => {
    try {
      downloadBlob(await fetchPdf(), filename);
    } catch {
      setNotice({ tone: "error", text: "The PDF could not be downloaded. Try again in a moment." });
    }
  };

  const columns: RegisterColumn<TimetableSlot>[] = [
    { header: "Day", cell: (row) => dayName(row.day_of_week), exportValue: (row) => dayName(row.day_of_week), className: "text-gray-700" },
    {
      header: "Time",
      cell: (row) => (periodMap.get(row.period) ? periodTime(periodMap.get(row.period)!) : row.period_name),
      exportValue: (row) => (periodMap.get(row.period) ? periodTime(periodMap.get(row.period)!) : row.period_name),
      className: "whitespace-nowrap tabular-nums text-gray-700",
    },
    { header: "Subject", cell: (row) => <span className="font-medium text-gray-900">{row.subject_name}</span>, exportValue: (row) => row.subject_name },
    canRequest
      ? { header: "Class", cell: (row) => row.class_name, exportValue: (row) => row.class_name, className: "text-gray-700" }
      : { header: "Teacher", cell: (row) => row.teacher_name, exportValue: (row) => row.teacher_name, className: "text-gray-700" },
    { header: "Room", cell: (row) => row.room || <span className="text-gray-300">Classroom</span>, exportValue: (row) => row.room || "Classroom", className: "text-gray-700" },
  ];

  if (slots.isError) {
    return (
      <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Your timetable could not be loaded. <button type="button" className="font-medium underline" onClick={() => slots.refetch()}>Try again</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My timetable</h1>
          <p className="mt-1 text-sm text-gray-500">{canRequest ? "Your lessons for the week. Open one for its details or to ask for a change." : "The week's lessons, teachers and rooms."}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canRequest && records.length > 0 && user && (
            <button type="button" onClick={() => download(() => timetableService.downloadTeacherTimetablePdf(user.id), `my-timetable-${user.username}.pdf`)} className="btn btn-outline gap-1.5">
              <Download className="h-4 w-4" /> Download PDF
            </button>
          )}
          {classes.map((item) => (
            <button key={item.id} type="button" onClick={() => download(() => timetableService.downloadClassTimetablePdf(item.id), `timetable-${item.name.replace(/\s+/g, "-")}.pdf`)} className="btn btn-outline gap-1.5">
              <Download className="h-4 w-4" /> {classes.length > 1 ? `${item.name} PDF` : "Download PDF"}
            </button>
          ))}
        </div>
      </div>

      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      {slots.isLoading ? (
        <div className="card"><div className="card-content h-64 animate-pulse rounded bg-gray-50" /></div>
      ) : !records.length ? (
        <div className="card">
          <div className="card-content py-10 text-center">
            <CalendarDays className="mx-auto h-10 w-10 text-gray-400" />
            <h2 className="mt-3 font-semibold text-gray-900">No timetable yet</h2>
            <p className="mt-1 text-sm text-gray-500">Lessons appear here once the Director of Studies publishes the teaching timetable.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {classes.length > 1 ? (
                <div role="group" aria-label="Class" className="segmented">
                  {classes.map((item) => (
                    <button key={item.id} type="button" aria-pressed={shownClass?.id === item.id} onClick={() => setClassChoice(item.id)} className="segmented-option">
                      {item.name}
                    </button>
                  ))}
                </div>
              ) : (
                <h2 className="font-semibold text-gray-900">{canRequest ? "Your week" : `${shownClass?.name ?? "The"} week`}</h2>
              )}
              <LayoutSwitch layout={layout} onChange={setLayout} />
            </div>
          </div>
          <div className="card-content">
            {layout === "periods" ? (
              <PeriodGrid
                slots={shown}
                periods={periodList}
                onOpen={setViewing}
                classLevel={classLevel}
                detail={(slot) => (canRequest ? slot.class_name : slot.teacher_name)}
              />
            ) : (
              <WeekGrid slots={shown} periods={periodList} onOpen={setViewing} detail={canRequest ? (slot) => slot.class_name : undefined} />
            )}
          </div>
        </div>
      )}

      {records.length > 0 && (
        <ClientRegister
          title="Lessons as a list"
          description="The same week, for searching, printing or saving."
          rows={records}
          config={lessonConfig(periodMap)}
          columns={columns}
          rowKey={(row) => row.id}
          onOpen={setViewing}
          noun={["lesson", "lessons"]}
          exportName={{ file: "my-timetable", title: "My Timetable" }}
          emptyText="No lessons."
        />
      )}

      {canRequest && (
        <RequestsPanel
          title="My change requests"
          description="What you have asked to change, and what was decided."
          actions={records.length > 0 && (
            <button type="button" onClick={() => { submit.reset(); setAsking({}); }} className="btn-primary h-8 min-h-8 px-3 text-xs">
              <Send className="mr-1.5 h-3.5 w-3.5" /> Ask for a change
            </button>
          )}
          rows={requests.data}
          periods={periodMap}
          isLoading={requests.isLoading}
          isError={requests.isError}
          onRetry={() => requests.refetch()}
          canDecide={false}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ["my-timetable-requests"] })}
        />
      )}

      {viewing && !asking && (() => {
        const period = periodMap.get(viewing.period);
        return (
          <DetailsModal
            size="xl"
            title={viewing.subject_name}
            headline={{ label: dayName(viewing.day_of_week), value: period ? periodTime(period) : viewing.period_name, note: viewing.period_name }}
            facts={[
              ["Class", viewing.class_name],
              ["Teacher", viewing.teacher_name],
              ["Room", viewing.room || "Their classroom"],
              ["Day", dayName(viewing.day_of_week)],
            ]}
            onClose={() => setViewing(null)}
            footer={
              <>
                <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
                {canRequest && (
                  <button type="button" className="btn btn-primary gap-1.5" onClick={() => { submit.reset(); setAsking({ slot: viewing.id }); }}>
                    <Send className="h-4 w-4" /> Ask to change this lesson
                  </button>
                )}
              </>
            }
          />
        );
      })()}

      {asking && (
        <RequestForm
          slots={records}
          periods={periodList}
          slot={asking.slot}
          busy={submit.isPending}
          error={submit.isError ? apiErrorMessage(submit.error, "The request could not be sent.") : undefined}
          onSend={(data) => submit.mutate(data)}
          onClose={() => { setAsking(null); setViewing(null); }}
        />
      )}
    </div>
  );
}

function RequestForm({ slots, periods, slot, busy, error, onSend, onClose }: {
  slots: TimetableSlot[];
  periods: TimetablePeriod[];
  slot?: number;
  busy: boolean;
  error?: string;
  onSend: (data: { request_type: TimetableRequest["request_type"]; current_slot: number; requested_changes: Record<string, unknown>; reason: string }) => void;
  onClose: () => void;
}) {
  const [lesson, setLesson] = useState(slot ? String(slot) : "");
  const [type, setType] = useState<TimetableRequest["request_type"]>("change_time");
  const [day, setDay] = useState("");
  const [period, setPeriod] = useState("");
  const [room, setRoom] = useState("");
  const [reason, setReason] = useState("");
  const current = slots.find((item) => String(item.id) === lesson);
  // Only lesson periods the class is at school for.
  const teaching = periods.filter((item) => isLesson(item) && !levelExcluded(item, current?.class_level));
  const unchanged = type === "change_time" && current && Number(day) === current.day_of_week && Number(period) === current.period;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (unchanged || reason.trim().length < 5) return;
    onSend({
      request_type: type,
      current_slot: Number(lesson),
      requested_changes: type === "change_time" ? { day_of_week: Number(day), period_id: Number(period) } : type === "change_room" ? { room: room.trim() } : {},
      reason: reason.trim(),
    });
  };
  return (
    <FormModal
      id="timetable-request-form"
      title="Ask for a timetable change"
      busy={busy}
      error={error}
      saveLabel="Send request"
      note="The Director of Studies decides; nothing moves until then."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The lesson" divided={false} columns={1}>
        <Field label="Lesson">
          <select required value={lesson} onChange={(event) => setLesson(event.target.value)} className="input mt-1">
            <option value="">Choose a lesson</option>
            {slots.map((item) => <option key={item.id} value={item.id}>{dayName(item.day_of_week)} · {item.period_name} · {item.subject_name} · {item.class_name}</option>)}
          </select>
        </Field>
      </FormSection>
      <FormSection title="The change" columns={1}>
        <div role="radiogroup" aria-label="Change" className="grid gap-2 sm:grid-cols-3">
          {REQUESTABLE.map(([value, label, help]) => (
            <label key={value} className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
              <input type="radio" name="request_type" value={value} checked={type === value} onChange={() => setType(value)} className="mt-0.5 accent-primary-600" />
              <span>
                <span className="block font-medium text-gray-900">{label}</span>
                <span className="block text-xs text-gray-500">{help}</span>
              </span>
            </label>
          ))}
        </div>
        {type === "change_time" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="New day">
              <select required value={day} onChange={(event) => setDay(event.target.value)} className="input mt-1">
                <option value="">Choose a day</option>
                {DAYS.slice(0, 5).map((name, index) => <option key={name} value={index}>{name}</option>)}
              </select>
            </Field>
            <Field label="New period" hint={unchanged ? <span className="text-amber-700">That is when it is now.</span> : undefined}>
              <select required value={period} onChange={(event) => setPeriod(event.target.value)} className="input mt-1">
                <option value="">Choose a period</option>
                {teaching.map((item) => <option key={item.id} value={item.id}>{item.name} · {periodTime(item)}</option>)}
              </select>
            </Field>
          </div>
        )}
        {type === "change_room" && (
          <Field label="New room">
            <input required value={room} onChange={(event) => setRoom(event.target.value)} className="input mt-1" placeholder="e.g. Science lab 2" />
          </Field>
        )}
        <Field as="div" label="Why it is needed" hint="At least 5 characters. The Director of Studies reads this.">
          <AIAssistField
            rows={3}
            value={reason}
            onChange={(next) => setReason(next)}
            placeholder="e.g. I teach S3 at the other campus on Wednesday mornings."
            context={{
              key: "timetable.request_note",
              params: { purpose: "request", slot: lesson || null, request_type: type, day_of_week: day || null, period_id: period || null, room: room || null },
            }}
            draftLabel="Draft the reason"
            draftHint={lesson ? "Reads the lesson and the change. Jot why first, and it writes it up." : "Choose the lesson first."}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}
